(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const records = new Map();
  const textSelector = 'h1,h2,h3,h4,h5,h6,p,label,legend,summary,.fs-kicker,.fs-btn,.fs-link,.button,.fs-card__price,.fs-card__meta';
  // Fall-Effekt für Texte und Bilder: Weg in px, Dauer und Versatz je Zeile in ms.
  const drop = {
    travel: { desktop: 60, mobile: 40 },
    duration: { desktop: 900, mobile: 700 },
    stagger: { desktop: 70, mobile: 45 },
    inCard: { desktop: 50, mobile: 35 }
  };
  const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';
  // Erst wenn ein Element diesen Puffer ganz verlassen hat, wird es neu scharf gestellt.
  const exitMargin = 140;
  // Bilder starten erst, wenn ein Viertel von ihnen zu sehen ist (höchstens ein Fünftel
  // der Fensterhöhe) — sonst ist der Fall vorbei, bevor man das Bild überhaupt sieht.
  const mediaShare = 0.25;
  const mediaViewportCap = 0.2;
  // Kleinere Bilder (Symbole, Zahlungslogos, Sterne) bleiben ruhig.
  const mediaMinSize = 80;
  let listening = false;
  let frame = 0;

  function motionDisabled() {
    return reduced.matches || window.Shopify?.designMode || document.body.dataset.fsMotion === 'none';
  }

  function modeFor(element) {
    if (motionDisabled()) return 'none';
    let scope = element.closest('[data-fs-motion]');
    while (scope && scope.dataset.fsMotion === 'inherit') {
      scope = scope.parentElement?.closest('[data-fs-motion]');
    }
    return scope?.dataset.fsMotion || document.body.dataset.fsMotion || 'none';
  }

  function isMedia(element) {
    return element.matches('.fs-card,.fs-tile,.fs-market__photo,.fs-story__figure,.fs-process__media,.fs-hero__gallery') || element.querySelector('img') !== null;
  }

  function motionFor(element, record) {
    const mode = (record.textDrop || record.media) && !motionDisabled() ? 'drop' : modeFor(element);
    if (mode === 'none' || typeof element.animate !== 'function') return null;
    const size = mobile.matches ? 'mobile' : 'desktop';
    // Eine eigene CSS-Verschiebung oder Drehung bleibt erhalten, der Fall kommt obendrauf.
    const base = getComputedStyle(element).transform;
    const baseTransform = base && base !== 'none' ? ` ${base}` : '';
    const rest = baseTransform.trim() || 'translate3d(0, 0, 0)';
    const baseY = baseTransform ? new DOMMatrixReadOnly(base).m42 : 0;
    if (mode === 'drop') {
      const startY = -drop.travel[size];
      return {
        startY,
        baseY,
        keyframes: [
          { opacity: 0.08, transform: `translate3d(0, ${startY}px, 0)${baseTransform}` },
          { opacity: 1, transform: rest }
        ],
        duration: drop.duration[size],
        delay: record.media
          ? Math.min(record.mediaIndex || 0, 4) * drop.stagger[size]
          : Math.min(record.textIndex, 4) * drop.stagger[size] + (record.inCard ? drop.inCard[size] : 0)
      };
    }
    const startY = mode === 'rise' ? Math.min(record.distance, size === 'mobile' ? 14 : 18) : 0;
    const initial = { opacity: 0 };
    const final = { opacity: 1 };
    if (mode === 'rise') {
      initial.transform = `translate3d(0, ${startY}px, 0)${baseTransform}`;
      final.transform = rest;
    }
    return {
      startY,
      baseY,
      keyframes: [initial, final],
      duration: Math.min(record.duration, size === 'mobile' ? 440 : 560),
      delay: Math.min(record.delay, size === 'mobile' ? 60 : 90)
    };
  }

  // Zurück in den Ruhezustand: Animation weg, Element steht an seinem Platz.
  function settle(element) {
    const record = records.get(element);
    if (!record) return;
    record.animation?.cancel();
    record.animation = undefined;
    record.state = 'idle';
    if (record.willChange !== undefined) {
      if (record.willChange) element.style.setProperty('will-change', record.willChange, record.willChangePriority);
      else element.style.removeProperty('will-change');
      record.willChange = undefined;
    }
  }

  // Scharf stellen: Element wartet angehalten in der Startlage, bis es ins Bild kommt.
  function arm(element) {
    const record = records.get(element);
    if (!record) return;
    settle(element);
    const motion = element.isConnected && motionFor(element, record);
    if (!motion) return;
    try {
      const animation = element.animate(motion.keyframes, { duration: motion.duration, delay: motion.delay, easing, fill: 'both' });
      animation.pause();
      animation.currentTime = 0;
      record.animation = animation;
      record.startY = motion.startY;
      record.baseY = motion.baseY;
      record.state = 'armed';
      record.willChange = element.style.getPropertyValue('will-change');
      record.willChangePriority = element.style.getPropertyPriority('will-change');
      element.style.willChange = motion.startY ? 'transform, opacity' : 'opacity';
    } catch {
      settle(element);
    }
  }

  function play(element) {
    const record = records.get(element);
    if (!record || record.state !== 'armed') return;
    const animation = record.animation;
    record.state = 'playing';
    animation.finished.then(() => { if (record.animation === animation) settle(element); }, () => {});
    animation.play();
  }

  // Verschiebung, die dieses Skript gerade selbst setzt. Sie wird aus der Position
  // herausgerechnet, damit weder der Fallweg noch das Abschneiden am Sektionsrand
  // als „Element hat das Bild verlassen" zählt — das ergab die Stotterschleife.
  function ownOffset(element, record) {
    if (record.state === 'armed') return record.startY;
    if (record.state !== 'playing') return 0;
    const transform = getComputedStyle(element).transform;
    return (transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0) - (record.baseY || 0);
  }

  function check() {
    frame = 0;
    const height = window.innerHeight || document.documentElement.clientHeight;
    const toPlay = [];
    const toArm = [];
    for (const [element, record] of records) {
      if (!element.isConnected) {
        records.delete(element);
        continue;
      }
      const bounds = element.getBoundingClientRect();
      if (!bounds.width || !bounds.height) continue;
      const offset = ownOffset(element, record);
      const top = bounds.top - offset;
      const bottom = bounds.bottom - offset;
      if (record.state === 'armed') {
        const visible = Math.min(bottom, height) - Math.max(top, 0);
        const needed = record.media ? Math.min(bounds.height * mediaShare, height * mediaViewportCap) : 1;
        if (visible >= needed) toPlay.push(element);
      } else if (bottom < -exitMargin || top > height + exitMargin) {
        toArm.push(element);
      }
    }
    toPlay.forEach(play);
    toArm.forEach(arm);
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(check);
  }

  function listen() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule, { once: true });
  }

  function init(scope = document) {
    if (motionDisabled()) return;
    // Gilt für den Hauptinhalt jeder Seite; Kopf- und Fußzeile bleiben ruhig.
    const main = document.querySelector('main');
    const candidates = new Map();
    const reveals = [...scope.querySelectorAll('[data-fs-reveal]')];
    if (scope instanceof Element && scope.matches('[data-fs-reveal]')) reveals.unshift(scope);
    for (const element of reveals) {
      const media = isMedia(element);
      if (main?.contains(element)) {
        if (media) {
          const imageArea = element.querySelector(':scope > .fs-card__media, :scope > .fs-tile__stack, :scope > .fs-tile__cover');
          if (imageArea) { candidates.set(imageArea, { media: true }); continue; }
        } else if (element.querySelector(textSelector)) continue;
      }
      candidates.set(element, { media });
    }
    const textRoot = main && (scope === document ? main : main.contains(scope) ? scope : null);
    if (textRoot) {
      // Jedes Bild fällt mit, auch ohne data-fs-reveal (z. B. der Bilderstreifen im CTA).
      const covered = [...candidates.keys(), ...records.keys()];
      for (const image of textRoot.querySelectorAll('img')) {
        let holder = image.closest('.fs-card__media, .fs-tile__stack, .fs-tile__cover') || image.parentElement;
        if (holder?.tagName === 'PICTURE') holder = holder.parentElement;
        if (!holder || holder === main || !main.contains(holder) || covered.some(element => element.contains(image))) continue;
        const size = holder.getBoundingClientRect();
        if (size.width < mediaMinSize || size.height < mediaMinSize) continue;
        candidates.set(holder, { media: true });
        covered.push(holder);
      }
    }
    if (textRoot) {
      const sectionCounts = new Map();
      const texts = [...textRoot.querySelectorAll(textSelector)];
      if (textRoot.matches(textSelector)) texts.unshift(textRoot);
      for (const element of texts) {
        if (!element.textContent?.trim()) continue;
        const section = element.closest('.shopify-section') || main;
        const textIndex = sectionCounts.get(section) || 0;
        sectionCounts.set(section, textIndex + 1);
        element.setAttribute('data-fs-home-text-drop', '');
        candidates.set(element, { media: false, textDrop: true, textIndex, inCard: !!element.closest('.fs-card,.fs-tile') });
      }
    }
    const height = window.innerHeight || document.documentElement.clientHeight;
    const width = document.documentElement.clientWidth;
    const added = [];

    for (const [element, options] of candidates) {
      if (records.has(element) || (!options.textDrop && modeFor(element) === 'none')) continue;
      // Bilder sind oft als Deko aria-hidden markiert, aber sichtbar — sie fallen trotzdem.
      if (element.closest(options.media ? '[hidden],script,style,template' : '[hidden],[aria-hidden="true"],.visually-hidden,script,style,template')) continue;
      let ancestor = element.parentElement;
      let nested = false;
      while (ancestor && ancestor !== document.body) {
        if (candidates.has(ancestor) || records.has(ancestor)) { nested = true; break; }
        ancestor = ancestor.parentElement;
      }
      if (nested) continue;
      const bounds = element.getBoundingClientRect();
      if (!bounds.width || !bounds.height) continue;
      // Was beim Laden schon sichtbar ist, steht still da und spielt erst nach dem
      // ersten Verlassen und Wiederkommen.
      const inView = bounds.bottom > 0 && bounds.top < height && bounds.right > 0 && bounds.left < width;
      const css = getComputedStyle(element);
      records.set(element, {
        ...options,
        state: 'idle',
        duration: Math.max(1, Number.parseFloat(css.getPropertyValue('--fs-motion-duration')) || 650),
        distance: Math.max(0, Number.parseFloat(css.getPropertyValue('--fs-motion-distance')) || 0),
        delay: Math.max(0, Number.parseFloat(css.getPropertyValue('--fs-delay')) || 0)
      });
      if (!inView) added.push(element);
    }

    // Bilder, die nebeneinander in einer Reihe stehen, fallen von links nach rechts versetzt.
    const rows = new Map();
    for (const [element, record] of records) {
      if (!record.media || record.mediaIndex !== undefined) continue;
      const bounds = element.getBoundingClientRect();
      const section = element.closest('.shopify-section') || main;
      const key = `${section?.id}|${Math.round((bounds.top + window.scrollY) / 40)}`;
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push([bounds.left, record]);
    }
    for (const row of rows.values()) {
      row.sort((a, b) => a[0] - b[0]).forEach(([, record], index) => { record.mediaIndex = index; });
    }

    added.forEach(arm);
    listen();
    schedule();
  }

  function cancel(scope = document) {
    for (const element of [...records.keys()]) {
      if (scope === document || scope.contains(element)) {
        settle(element);
        records.delete(element);
      }
    }
  }

  // Nachgeladene Inhalte (Bewertungen, Filterergebnisse, Warenkorb) werden nachgemeldet.
  // Was dabei schon im Bild steht, bleibt ruhig; nur Späteres fällt beim Hereinscrollen.
  let pending = 0;
  function watch() {
    const main = document.querySelector('main');
    if (!main || typeof MutationObserver !== 'function') return;
    new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(() => init(main), 200);
    }).observe(main, { childList: true, subtree: true });
  }

  function start() {
    init();
    watch();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('focusin', event => {
    for (const element of records.keys()) {
      if (element === event.target || element.contains(event.target)) settle(element);
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => cancel(event.target));
  document.addEventListener('shopify:block:select', event => cancel(event.target));
  document.addEventListener('shopify:section:select', event => cancel(event.target));
  window.addEventListener('pagehide', () => cancel());
  window.addEventListener('pageshow', event => { if (event.persisted) init(); });
  reduced.addEventListener('change', () => {
    cancel();
    if (!reduced.matches) init();
  });
})();
